import axiosInstance from "../config/axiosInstance";
import { ApiRoutes } from "../constants/ApiRoutes";
import { QueryKeys } from "../constants/QueryKeys";
import { MutateKeys } from "../constants/MutateKeys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buildFetchTicketJobsUrl,
  cleanParams,
  encodeArrayObjects,
} from "../Utilities/queryParamUtils";
import { generatePath } from "react-router-dom";

interface UploadTicketListingParamsProp {
  search?: string;
  page?: number;
  per_page?: number;
  companyIds?: string[];
  truckIds?: string[];
  orderIds?: string[];
  jobNumbers?: string[];
  jobNames?: string[];
  fromDate?: string;
  toDate?: string;
}

export interface TicketLockStatusParams {
  id: string;
  locked: boolean;
}

export interface TicketUploadProps {
  materialId: string;
  vendorId: string;
  companyId: string;
  orderId: string;
  jobId: string;
}

const TicketUploadServices = () => {
  const queryClient = useQueryClient();
  const {
    FETCH_ALL_TICKETS,
    FETCH_TICKET_TRUCKS_META,
    FETCH_TICKET_CUSTOMERS_META,
    FETCH_TICKET_JOBS_META,
    FETCH_TICKET_ORDERS_META,
    FETCH_TICKET_MATERIALS,
    FETCH_TICKET_VENDORS,
    FETCH_TICKET_ORDER_NUMBER_META,
    SAVE_TICKET,
    FETCH_TICKET_DETAILS,
    SAVE_DATA_INPUT,
    REPLACE_TICKET,
    REPLACE_FILE,
    UPDATE_TICKET_LOCK_ROUTE,
  } = ApiRoutes;
  const {
    FETCH_TICKETS_KEY,
    FETCH_TICKET_CUSTOMERS_KEY,
    FETCH_TICKET_TRUCKS_KEY,
    FETCH_TICKET_JOBS_KEY,
    FETCH_TICKET_ORDERS_KEY,
    FETCH_TICKET_FILTERS_KEY,
    FETCH_TICKET_MATERIALS_KEY,
    FETCH_TICKET_VENDORS_KEY,
    FETCH_TICKET_ORDER_NUMBER_KEY,
    FETCH_TICKET_DETAILS_KEY,
  } = QueryKeys;
  const {
    SAVE_TICKET_MUTATE_KEY,
    SAVE_DATA_INPUT_KEY,
    REPLACE_TICKET_KEY,
    REPLACE_FILE_KEY,
    UPDATE_TICKET_LOCK_KEY,
  } = MutateKeys;

  const FetchTicketUploadCustomers = (
    ticketUploadParams: TicketUploadProps,
  ) => {
    const fetchTicketUploadCustomers = async () => {
      const { data } = await axiosInstance.get(FETCH_TICKET_CUSTOMERS_META, {
        params: ticketUploadParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_TICKET_CUSTOMERS_KEY],
      queryFn: fetchTicketUploadCustomers,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchTicketUploadJobs = (ticketUploadParams: TicketUploadProps) => {
    const fetchTicketUploadJobs = async () => {
      const { data } = await axiosInstance.get(FETCH_TICKET_JOBS_META, {
        params: ticketUploadParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_TICKET_JOBS_KEY],
      queryFn: fetchTicketUploadJobs,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchTicketOrderNumbersMeta = (
    ticketUploadParams: TicketUploadProps,
  ) => {
    const fetchTicketOrderNumberMeta = async () => {
      const { data } = await axiosInstance.get(FETCH_TICKET_ORDER_NUMBER_META, {
        params: ticketUploadParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_TICKET_ORDER_NUMBER_KEY],
      queryFn: fetchTicketOrderNumberMeta,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchTicketUploadMaterial = (ticketUploadParams: TicketUploadProps) => {
    const fetchTicketUploadMaterial = async () => {
      const { data } = await axiosInstance.get(FETCH_TICKET_MATERIALS, {
        params: ticketUploadParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_TICKET_MATERIALS_KEY],
      queryFn: fetchTicketUploadMaterial,
      refetchOnWindowFocus: false,
    });
  };

  const FetchTicketUploadVendor = (ticketUploadParams: TicketUploadProps) => {
    const fetchTicketUploadVendor = async () => {
      const { data } = await axiosInstance.get(FETCH_TICKET_VENDORS, {
        params: ticketUploadParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_TICKET_VENDORS_KEY],
      queryFn: fetchTicketUploadVendor,
      refetchOnWindowFocus: false,
    });
  };

  const FetchUploadedTickets = (
    params?: UploadTicketListingParamsProp,
    pageSizeChanged: boolean,
    enabledFlag: boolean,
  ) => {
    const fetchUploadedTickets = async () => {
      const cleanedParams = cleanParams({
        ...params,
        setDefaultPageSize: pageSizeChanged,
      });
      const encodedParams = encodeArrayObjects(cleanedParams);
      const { data } = await axiosInstance.get(FETCH_ALL_TICKETS, {
        params: {
          ...encodedParams,
        },
      });
      return data;
    };

    return useQuery({
      queryKey: [FETCH_TICKETS_KEY],
      queryFn: fetchUploadedTickets,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!enabledFlag,
    });
  };

  const FetchTicketUploadTrucks = () => {
    const fetchTicketUploadTrucks = async () => {
      const { data } = await axiosInstance.get(FETCH_TICKET_TRUCKS_META);
      return data;
    };
    return useQuery({
      queryKey: [FETCH_TICKET_TRUCKS_KEY],
      queryFn: fetchTicketUploadTrucks,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const FetchTicketUploadOrders = () => {
    const fetchTicketUploadOrders = async () => {
      const { data } = await axiosInstance.get(FETCH_TICKET_ORDERS_META);
      return data;
    };
    return useQuery({
      queryKey: [FETCH_TICKET_ORDERS_KEY],
      queryFn: fetchTicketUploadOrders,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: false,
    });
  };

  const FetchTicketUploadFilterMeta = () => {
    const fetchTicketFilterOptions = async () => {
      const { data } = await axiosInstance.get(FETCH_TICKET_ORDERS_META);
      return data;
    };
    return useQuery({
      queryKey: [FETCH_TICKET_FILTERS_KEY],
      queryFn: fetchTicketFilterOptions,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    });
  };

  const SaveTicket = () => {
    const saveTicket = async (saveTicketRequestBody) => {
      const payload = {
        entityData: {
          ...saveTicketRequestBody,
        },
      };
      const { data } = await axiosInstance.post(SAVE_TICKET, payload);
      return data;
    };
    return useMutation({
      mutationKey: [SAVE_TICKET_MUTATE_KEY],
      mutationFn: saveTicket,
    });
  };

  const FetchTicketDetails = ({ id }) => {
    const fetchTicketDetails = async () => {
      const url = generatePath(FETCH_TICKET_DETAILS, { id: id });
      const { data } = await axiosInstance.get(url);
      return data;
    };
    return useQuery({
      queryKey: [FETCH_TICKET_DETAILS_KEY],
      queryFn: fetchTicketDetails,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
  };

  const SaveSubmitForDataInput = () => {
    const saveSubmitForDataInput = async (submitForDataInputBody) => {
      const { id, ...dataInputPaylod } = submitForDataInputBody;
      const payload = {
        entityData: {
          ...dataInputPaylod,
        },
      };
      const url = generatePath(SAVE_DATA_INPUT, { id: id });

      const { data } = await axiosInstance.put(url, payload);
      return data;
    };
    return useMutation({
      mutationKey: [SAVE_DATA_INPUT_KEY],
      mutationFn: saveSubmitForDataInput,
    });
  };

  const ReplaceTicket = () => {
    const replaceTicket = async (replaceTicketPayload) => {
      const payload = {
        entityData: {
          ...replaceTicketPayload,
        },
      };
      const { data } = await axiosInstance.post(REPLACE_TICKET, payload);
      return data;
    };
    return useMutation({
      mutationKey: [REPLACE_TICKET_KEY],
      mutationFn: replaceTicket,
    });
  };

  const ReplaceFile = () => {
    const replaceFile = async (replaceFilePayload) => {
      const payload = {
        entityData: {
          ...replaceFilePayload,
        },
      };
      const { data } = await axiosInstance.post(REPLACE_FILE, payload);
      return data;
    };
    return useMutation({
      mutationKey: [REPLACE_FILE_KEY],
      mutationFn: replaceFile,
    });
  };

  const UpdateTicketLockStatus = () => {
    const updateTicketLockStatus = async (params: TicketLockStatusParams) => {
      const payload = {
        entityData: {
          locked: params?.locked,
        },
      };
      const url = generatePath(UPDATE_TICKET_LOCK_ROUTE, {
        id: params?.id,
      });
      const { data } = await axiosInstance.patch(url, payload);
      return data;
    };
    return useMutation({
      mutationKey: [UPDATE_TICKET_LOCK_KEY],
      mutationFn: updateTicketLockStatus,
      onSuccess: (updatedTicket) => {
        queryClient.setQueriesData(
          { queryKey: [FETCH_TICKETS_KEY] },
          (oldData) => {
            if (!oldData) return;
            const updatedList = oldData?.data?.map((ticket) =>
              ticket?.id === updatedTicket?.data?.id
                ? updatedTicket?.data
                : ticket,
            );
            return {
              ...oldData,
              data: updatedList,
            };
          },
        );
      },
    });
  };

  return {
    FetchUploadedTickets,
    FetchTicketUploadCustomers,
    FetchTicketUploadJobs,
    FetchTicketUploadOrders,
    FetchTicketUploadMaterial,
    FetchTicketUploadVendor,
    FetchTicketUploadTrucks,
    FetchTicketUploadFilterMeta,
    FetchTicketOrderNumbersMeta,
    SaveTicket,
    FetchTicketDetails,
    SaveSubmitForDataInput,
    ReplaceTicket,
    ReplaceFile,
    UpdateTicketLockStatus,
  };
};

export default TicketUploadServices;
